import { PermissionEnum } from '@Application/Security/Roles';
import PageHeader from '@Guidelines/Layout/PageHeader';
import BeneficiariesListContent from '@UI/Pages/Beneficiaries/BeneficiaryList/BeneficiariesListContent';
import BeneficiariesNotifications from '@UI/Pages/Beneficiaries/Shared/BeneficiariesNotifications';
import Filters from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters';
import UpsertBeneficiarySidebar from '@UI/Pages/Beneficiaries/Sidebars/UpsertBeneficiarySidebar';
import { openBeneficiarySidebar } from '@UI/Pages/Beneficiaries/Signals/beneficiarySidebarSignal';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import PageContainer from '@Guidelines/Layout/PageContainer';
import { BreadcrumbActions } from '@verclam/breadcrumbs';

/**
 * Assembles the page: header, `Filters` (no props — it reads its own signal), the data-owning
 * `BeneficiariesListContent`, `BeneficiarySidebar` and notifications, in that visual order. Fetches
 * nothing itself: the header's dossier count reads `beneficiariesStore.beneficiaries` through a plain
 * `useSelector`, the same value `BeneficiariesListContent` populates through its own `useStoreQuery`.
 */
const BeneficiariesListPage: React.FC = () => {
	const beneficiariesList = useSelector((state) => state.beneficiariesStore.beneficiaries);

	const handleNewBeneficiaryClick = React.useCallback(() => {
		openBeneficiarySidebar();
	}, []);

	const description = React.useMemo(() => {
		if (beneficiariesList === null) {
			return undefined;
		}

		const count = beneficiariesList.totalRecords;

		return `${count} dossier${count > 1 ? 's' : ''}`;
	}, [beneficiariesList]);

	return (
		<PageContainer scrollY={'scroll'}>
			<WithPermission permission={PermissionEnum.SUBJECT_WRITE}>
				<BreadcrumbActions>
					<Button
						type="button"
						text
						label="Nouveau bénéficiaire"
						icon={<Plus size={16} />}
						onClick={handleNewBeneficiaryClick}
					/>
				</BreadcrumbActions>
			</WithPermission>
			<PageHeader title="Bénéficiaires" description={description} />
			<Filters />
			<BeneficiariesListContent />
			<UpsertBeneficiarySidebar />
			<BeneficiariesNotifications />
		</PageContainer>
	);
};

export default BeneficiariesListPage;
